import { StrictMode, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider, type ThemeMode } from "@studio";
import { ApiError } from "./lib/api";
import { setPrincipalChangeHandler } from "./lib/auth";
import { storedThemeMode } from "./console/freshness";
import { inBridge } from "./lib/native";
import { router } from "./router";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2_000,
      refetchOnWindowFocus: true,
      // 권한·없는 자원 오류는 다시 시도해도 같다.
      retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
    },
  },
});

setPrincipalChangeHandler(() => queryClient.clear());

/** ThemeProvider 는 mode 를 넘기면 그 값으로 고정된다(제어 컴포넌트). 토글이 바로 먹도록 모드를 여기서 상태로 가진다. */
function Theme({ children }: { children: ReactNode }) {
  // 데스크톱 앱(머신)은 처음에 다크 (2026-10-20 사용자 요청). 사용자가 고른 값이 있으면 그것을 따른다.
  const [mode, setMode] = useState<ThemeMode>(() => storedThemeMode() ?? (inBridge() ? "dark" : "system"));
  return (
    <ThemeProvider template="classic" design="original" mode={mode} onModeChange={setMode}>
      {children}
    </ThemeProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* 시안 6a084bb: 웹·데스크톱 모두 다크 지원. 데스크톱은 OS 테마, 웹은 OS 테마 기본에 헤더 토글로 바꿀 수 있다. */}
    <Theme>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Theme>
  </StrictMode>,
);
